<template>
  <div class="w-100">
    <!-- Import dialog: pick a CSV, then upload it. -->
    <v-dialog v-model="dialog" max-width="700px">
      <v-card prepend-icon="mdi-file-delimited" title="Import a CSV file">
        <v-card-text>
          <p class="mb-3">
            Rows are matched on the <code>id</code> column: an existing
            <code>id</code> updates the client, a blank <code>id</code> creates a
            new one. Export the data first to get a file in the right format.
          </p>

          <DragAndDropComponent
            ref="dropzone"
            accept=".csv, text/csv"
            @files-changed="onFilesChanged"
          />

          <v-alert
            v-if="importErrors.length"
            type="warning"
            variant="tonal"
            class="mt-4"
          >
            <ul class="ps-4">
              <li v-for="(err, i) in importErrors" :key="i">{{ err }}</li>
            </ul>
          </v-alert>
        </v-card-text>

        <template v-slot:actions>
          <v-spacer></v-spacer>
          <v-btn text="Cancel" @click="closeDialog"></v-btn>
          <v-btn
            color="primary"
            text="Import"
            :disabled="!selectedFile"
            :loading="importing"
            @click="uploadImport"
          ></v-btn>
        </template>
      </v-card>
    </v-dialog>

    <v-bottom-navigation grow color="primary">
      <v-btn @click="dialog = true" prepend-icon="mdi-upload">
        Import data
      </v-btn>

      <v-btn
        :loading="loading"
        @click="downloadExport"
        prepend-icon="mdi-download"
      >
        Export data

        <template v-slot:loader>
          <v-progress-linear indeterminate></v-progress-linear>
        </template>
      </v-btn>
    </v-bottom-navigation>

    <v-snackbar v-model="snackbar">
      {{ alertText }}
      <template v-slot:actions>
        <v-btn color="pink" variant="text" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import CustomerService from "@/services/CustomerService";

export default {
  // Emitted after a successful import so the parent can refresh the table.
  emits: ["imported"],

  data: () => ({
    loading: false,
    importing: false,
    dialog: false,
    snackbar: false,
    alertText: "",
    selectedFile: null,
    importErrors: [],
  }),

  methods: {
    onFilesChanged(files) {
      this.selectedFile = files && files.length ? files[0] : null;
      this.importErrors = [];
    },

    closeDialog() {
      this.dialog = false;
      this.selectedFile = null;
      this.importErrors = [];
      if (this.$refs.dropzone) this.$refs.dropzone.reset();
    },

    async downloadExport() {
      this.loading = true;
      try {
        const csv = await CustomerService.downloadExport();

        // Prepend a BOM so Excel opens the accented columns correctly.
        const blob = new Blob(["\ufeff" + csv], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "customers-export.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        this.alertText = "Export complete.";
      } catch (e) {
        this.alertText = "Error exporting the data.";
      } finally {
        this.loading = false;
        this.snackbar = true;
      }
    },

    async uploadImport() {
      if (!this.selectedFile) return;

      this.importing = true;
      this.importErrors = [];
      try {
        const csvText = await this.selectedFile.text();
        const result = await CustomerService.importCustomers(csvText);

        this.alertText = (result && result.message) || "Import complete.";
        this.importErrors = (result && result.errors) || [];

        // Let the parent reload the table.
        this.$emit("imported");

        // Keep the dialog open when there are row errors to show.
        if (!this.importErrors.length) this.closeDialog();
      } catch (e) {
        this.alertText =
          "Error during import: " + (e.message || "invalid file.");
      } finally {
        this.importing = false;
        this.snackbar = true;
      }
    },
  },
};
</script>
